import { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { Box, Button, Typography } from '@mui/material';
import PacmanLoader from 'react-spinners/PacmanLoader';
import { useDomain } from '../../../states/AppDomainProvider';
import { getUserToken } from '../../../utils/VizzleSessionStorage';
import { isTeacher } from '../../../utils/User';
import { URL_LESSONS, URL_LOGIN } from '../../../routes/RouteConstants';
import Logger from '../../../utils/Logger';
import { logout } from '../../../services/LoginService';
import VizzleLogo from '../../../vizzle_NoTag.svg';

export const STATUS = {
  resolving: 'resolving',
  notTeacher: 'not-teacher',
  noAccess: 'no-access',
  notFound: 'not-found',
  error: 'error',
};

const getMessages = (itemName) => ({
  [STATUS.resolving]: {
    title: 'Loading, please wait',
  },
  [STATUS.notTeacher]: {
    title: `This ${itemName} detail plan is only available to educator accounts.`,
    detail: 'Please sign in with an educator account to view it.',
  },
  [STATUS.noAccess]: {
    title: `You do not have access to this ${itemName}.`,
    detail: `This ${itemName} is part of content that is not included in your account. Please contact your administrator if you believe you should have access.`,
  },
  [STATUS.notFound]: {
    title: `This ${itemName} could not be found.`,
    detail: 'It may have been removed or the link may be out of date.',
  },
  [STATUS.error]: {
    title: `This ${itemName} detail plan could not be opened.`,
    detail: 'Please try again later.',
  },
});

/**
 * Shared landing page for public "Detail Plan Link" URLs.
 *
 * - No session: send the user to login and come back to `returnPath` afterwards.
 * - Teacher session: call `resolveTarget` to get the path inside the new Vizzle iframe,
 *   then open the Lessons page with the iframe pointed at it.
 * - Any other session (student): explain that the page is for educator accounts.
 *
 * `resolveTarget` may throw an axios error; 403 maps to "no access",
 * 400/404 to "not found", anything else to a generic error.
 */
export default function DetailPlanLinkLanding({ itemName, returnPath, resolveTarget }) {
  const navigate = useNavigate();
  const { userDomain } = useDomain();
  const { user } = userDomain.domainData;

  const userToken = getUserToken();
  const teacher = Boolean(userToken) && isTeacher(user);
  const [status, setStatus] = useState(teacher ? STATUS.resolving : STATUS.notTeacher);

  useEffect(() => {
    let cancelled = false;
    const openDetailPlan = async () => {
      if (!teacher) {
        setStatus(STATUS.notTeacher);
        return;
      }
      try {
        const target = await resolveTarget();
        if (cancelled) {
          return;
        }
        if (!target) {
          setStatus(STATUS.notFound);
          return;
        }
        navigate(URL_LESSONS, {
          replace: true,
          state: {
            goToUrl: target,
          },
        });
      } catch (e) {
        Logger.logError(e);
        if (cancelled) {
          return;
        }
        const httpStatus = e?.response?.status;
        if (httpStatus === 403) {
          setStatus(STATUS.noAccess);
        } else if (httpStatus === 404 || httpStatus === 400) {
          setStatus(STATUS.notFound);
        } else {
          setStatus(STATUS.error);
        }
      }
    };
    openDetailPlan();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teacher, returnPath]);

  if (!userToken) {
    return (
      <Navigate
        to={URL_LOGIN}
        replace
        state={{
          redirecTo: returnPath,
        }}
      />
    );
  }

  const { title, detail } = getMessages(itemName)[status];

  return (
    <Box display='flex' flexDirection='column' width='100vw' height='100vh' data-test='detail-plan-link' data-status={status}>
      <Box mx='auto' mt='120px'>
        <img src={VizzleLogo} alt='Vizzle Logo' width='250px' />
      </Box>
      <Box mt='50px' mx='auto' maxWidth='600px' textAlign='center'>
        {status === STATUS.resolving && (
          <Box width='100px' height='100px' mx='auto'>
            <PacmanLoader size={50} color='#3b94d1' css={{ margin: 'auto' }} />
          </Box>
        )}
        <Typography variant='h5' gutterBottom data-test='detail-plan-link-title'>
          {title}
        </Typography>
        {detail && (
          <Typography variant='body1' data-test='detail-plan-link-detail'>
            {detail}
          </Typography>
        )}
        {status === STATUS.notTeacher && (
          <Button
            variant='contained'
            className='btn-primary'
            sx={{ mt: 3 }}
            onClick={() => { logout(); }}
            data-test='detail-plan-link-logout-action'
          >
            Log out
          </Button>
        )}
      </Box>
    </Box>
  );
}
